import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Switch } from "@/shared/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";
import { cn } from "@/shared/lib/utils";
import { useTranslation } from "@/shared/i18n";
import type { CreatorBankAccount, UpdateCreatorBankAccountPayload, CreateCreatorBankAccountPayload } from "../types";
import { useUpdateCreatorFinancialBankAccount, useCreateCreatorFinancialBankAccount } from "../model/hooks";
import { useBrasilApiBanks } from "@/features/payments/hooks";

const normalizeText = (text: string) =>
  text.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");

const bankFilter = (value: string, search: string): number => {
  if (!search?.trim()) return 1;
  return normalizeText(value).includes(normalizeText(search.trim())) ? 1 : 0;
};

const schema = z.object({
  bank: z.string().min(2),
  code: z.string().min(1),
  branch: z.string().min(1),
  account: z.string().min(1),
  pix: z.string().min(3),
  default: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export type BankAccountFormMode = "create" | "edit";

interface EditBankAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: BankAccountFormMode;
  account: CreatorBankAccount | null;
  onSuccess?: () => void;
}

export function EditBankAccountDialog({
  open,
  onOpenChange,
  mode,
  account,
  onSuccess,
}: EditBankAccountDialogProps) {
  const { t } = useTranslation("creator-financial");
  const { updateBankAccount, isLoading: isUpdating } = useUpdateCreatorFinancialBankAccount();
  const { createBankAccount, isLoading: isCreating } = useCreateCreatorFinancialBankAccount();
  const { data: banks, isLoading: loadingBanks } = useBrasilApiBanks();
  const [bankOpen, setBankOpen] = useState(false);
  const isLoading = isUpdating || isCreating;
  const isEdit = mode === "edit";

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      bank: "",
      code: "",
      branch: "",
      account: "",
      pix: "",
      default: false,
    },
  });

  useEffect(() => {
    if (open) {
      if (account) {
        form.reset({
          bank: account.bank,
          code: account.code,
          branch: account.branch,
          account: account.account,
          pix: account.pix,
          default: account.default,
        });
      } else {
        form.reset({
          bank: "",
          code: "",
          branch: "",
          account: "",
          pix: "",
          default: false,
        });
      }
    }
  }, [open, account, form]);

  const bankOptions = useMemo(() => {
    if (!banks) return [];
    return banks.map((b) => ({
      value: b.code?.toString() ?? "",
      label: b.name,
      fullName: b.fullName,
    }));
  }, [banks]);

  const selectedCode = form.watch("code");
  const selectedBank = useMemo(
    () => bankOptions.find((o) => o.value === selectedCode) ?? null,
    [bankOptions, selectedCode]
  );

  const handleSubmit = async (values: FormValues) => {
    const payload: CreateCreatorBankAccountPayload = {
      bank: values.bank,
      code: values.code,
      branch: values.branch,
      account: values.account,
      pix: values.pix,
      default: values.default,
    };
    if (isEdit && account) {
      await updateBankAccount({ id: account.id, payload });
    } else {
      await createBankAccount(payload);
    }
    onSuccess?.();
    onOpenChange(false);
  };

  const handleBankSelect = (code: string) => {
    const opt = bankOptions.find((o) => o.value === code);
    if (opt) {
      form.setValue("bank", opt.label);
      form.setValue("code", code);
    }
    setBankOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("editBankAccount.title") : t("createBankAccount.title")}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isEdit ? t("editBankAccount.subtitle") : t("createBankAccount.subtitle")}
          </p>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">
                {t("editBankAccount.accountInfo")}
              </h4>
              <p className="text-xs text-muted-foreground">
                {t("editBankAccount.accountInfoDesc")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("editBankAccount.bank")}</FormLabel>
                  <Popover modal open={bankOpen} onOpenChange={setBankOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            "w-full justify-between gap-2",
                            !field.value && "text-muted-foreground"
                          )}
                          disabled={loadingBanks}
                        >
                          {loadingBanks ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : selectedBank ? (
                            <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
                              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                                {selectedBank.value.padStart(3, "0")}
                              </span>
                              <span className="truncate">
                                {selectedBank.fullName || selectedBank.label}
                              </span>
                            </span>
                          ) : (
                            t("editBankAccount.bankHint")
                          )}
                          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command filter={bankFilter} shouldFilter>
                        <CommandInput placeholder={t("editBankAccount.bankHint")} />
                        <CommandList>
                          <CommandEmpty>Nenhum banco encontrado.</CommandEmpty>
                          <CommandGroup>
                            {bankOptions.map((opt) => (
                              <CommandItem
                                key={opt.value}
                                value={`${opt.label} ${opt.value} ${opt.fullName}`}
                                onSelect={() => {
                                  field.onChange(opt.value);
                                  handleBankSelect(opt.value);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4 shrink-0",
                                    field.value === opt.value ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <span className="mr-2 shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                                  {opt.value.padStart(3, "0")}
                                </span>
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <span className="truncate font-medium">
                                    {opt.fullName || opt.label}
                                  </span>
                                  {opt.label !== opt.fullName && opt.label !== opt.value && (
                                    <span className="truncate text-xs text-muted-foreground">
                                      {opt.label}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="branch"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("editBankAccount.branch")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="0001" />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t("editBankAccount.branchHint")}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("editBankAccount.accountNumber")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="12345-6" />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t("editBankAccount.accountHint")}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pix"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("editBankAccount.pix")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="email@exemplo.com" />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t("editBankAccount.pixHint")}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="default"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("editBankAccount.setAsDefault")}
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      {t("editBankAccount.setAsDefaultDesc")}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("editBankAccount.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEdit ? t("editBankAccount.saving") : t("createBankAccount.saving")}
                  </>
                ) : isEdit ? (
                  t("editBankAccount.save")
                ) : (
                  t("createBankAccount.save")
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
